import { useMutation, useQuery } from "@tanstack/react-query";
import { ColDef, ColGroupDef } from "ag-grid-community";
import dayjs from "dayjs";
import {
  ChangeEvent,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Controller, useForm } from "react-hook-form";
import * as XLSX from "xlsx";

import { ReactComponent as IconPlus } from "../../../styles/assets/svg/icon_add.svg";
import { ReactComponent as IconDelete } from "../../../styles/assets/svg/icon_edit_delete.svg";

import * as S from "./ChartContent.style";

import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useRecoilState, useRecoilValue } from "recoil";
import { chartTabseState } from "../../../common/states/chart";
import { currentShipState } from "../../../common/states/ship";
import { ChartOption, ChartTime, TrendChart } from "../../../types/chart";
import { request } from "../../../common/api";
import { showAlert } from "../Alert";
import { fileDownload } from "../../../common/functions/download";
import { validateDateRange } from "../../../common/util/dateUtils";
import { MachinerySelect } from "../Select/MachinerySelect";
import { LowMachinerySelect } from "../Select/LowMachinerySelect";
import { SelectOption } from "../../../common/types/selectOption";
import { GET_SENSOR_SELECT_OPTIONS } from "../../../common/query-keys";
import { SensorSelect } from "../Select/SensorSelect";
import { InputText } from "../../atoms/Inputs/InputText";
import { Buttons } from "../../atoms/Buttons";
import { SearchBar } from "../../molecules/SearchBar";
import { MultiAxesLineChart } from "../../atoms/Charts/MultiAxesLineChart";
import { Grid } from "../../molecules/Grid";
import { ModalWarn } from "../../molecules/Modals/ModalWarn";

export interface ChartContentSearchInputs {
  chtTrdShiptypeNo?: string;
  chtTrdShiptypeName?: string;
  chtTrdShipNo?: string;
  chtTrdShipName?: string;
  chtTrdStdTime?: string;
  chtTrdEndTime?: string;
}

interface ChartContentProps {
  trendId?: number;
  updateTrendId: (trendId: number) => void;
  newCharts: any;
  newChartOptions: any;
  newChartSearch: any;
  selectedTab: number;
}

const schema = yup.object({
  chartTimes: yup.array().of(
    yup.object().shape({
      chtMcyName: yup.string().required("장비를 선택해주세요."),
      chtLowMcyName: yup.string().required("계통을 선택해주세요."),
      chtCode: yup.string().required("센서를 선택해주세요."),
      chtColor: yup.string().required("색상을 선택해주세요."),
    })
  ),
});

const PRESET_COLORS = [
  "#ff6b6b", // 빨간색
  "#faa14d", // 주황색
  "#ffeead", // 노란색
  "#98fb98", // 연두색
  "#4ecdc4", // 청록색
  "#45b7d1", // 하늘색
  "#9370db", // 보라색
  "#242424", // 흑색
  "#8f6f33", // 갈색
  "#d4a5a5", // 분홍색
];

const ChartContent = ({
  trendId,
  updateTrendId,
  newCharts,
  newChartOptions,
  newChartSearch,
  selectedTab,
}: ChartContentProps) => {
  console.log("@TrendId: ", trendId);
  // 새 탭 추가 후 차트 생성시 검색 조건에 추가하기 위함
  const [chartTabs, setChartTabs] = useRecoilState(chartTabseState);

  const shipStatus = useRecoilValue(currentShipState);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // 검색 조건 초기 값
  const searchDefaultValues = {
    chtTrdShiptypeNo: shipStatus.shipTypeNo || "", // 함정 정보 값 선택시 반영
    chtTrdShiptypeName: shipStatus.shipTypeName || "",
    chtTrdShipNo: shipStatus.shipNo || "",
    chtTrdShipName: shipStatus.shipName || "",
    chtTrdStdTime: "",
    chtTrdEndTime: "",
  };

  const [chartOptions, setChartOptions] = useState<ChartOption[]>([]);
  const [modalSaveShow, setModalSaveShow] = useState(false);
  const [modalUploadShow, setModalUploadShow] = useState(false);
  const [modalWarnShow, setModalWarnShow] = useState(false);
  const [chartRenderLoading, setChartRenderLoading] = useState(false);
  const [isExcelLoading, setIsExcelLoading] = useState(false);

  const { control, watch, setValue, reset, handleSubmit, trigger } =
    useForm<any>({
      resolver: yupResolver(schema),
    });

  // const { fields, append, remove } = useFieldArray({
  //   control,
  //   name: "chartTimes",
  // });

  const [search, setSearch] =
    useState<ChartContentSearchInputs>(searchDefaultValues);

  /** 🟢 차트 조건 조회 */
  const chartConditionDataHandle = async (id: number) => {
    const response = await request<any>({
      method: "GET",
      url: `/chart/condition/${id}`,
    });

    if (response) {
      reset(response);
      setSearch({
        ...response?.chartTrend,
        chtTrdStdTime: dayjs(response?.chartTrend?.chtTrdStdTime).format(
          "YYYY-MM-DD HH:mm:ss"
        ),
        chtTrdEndTime: dayjs(response?.chartTrend?.chtTrdEndTime).format(
          "YYYY-MM-DD HH:mm:ss"
        ),
      });
      createChart(response?.chartTimes, response?.chartTrend);
    } else {
      reset({ chartTimes: [] });
      setChartOptions([]);
      createChart([], searchDefaultValues);
    }

    return response;
  };

  const changeTradeId = async (data: { id: number }) => {
    updateTrendId(data.id);
    await chartConditionDataHandle(data.id);
  };

  // 탭마다 차트 초기화 또는 패치
  useEffect(() => {
    if (trendId !== undefined && trendId) {
      chartConditionDataHandle(trendId);
      return;
    }

    if (
      chartTabs.find((tab) => tab.index === selectedTab) &&
      newCharts.length === 0
    ) {
      reset({ chartTimes: [] });
      setChartOptions([]);
      setSearch(searchDefaultValues);
    } else {
      console.log("newCharts: ", newCharts);
      reset({ chartTimes: newCharts });
      setChartOptions(newChartOptions);
      setSearch(newChartSearch);

      if (newChartOptions.length === 0) {
        setSearch(searchDefaultValues);
        setChartTabs((prevTabs) =>
          prevTabs.map((tab) =>
            tab.index !== selectedTab
              ? tab
              : {
                  ...tab,
                  chartOptions: [],
                  chartTimes: [],
                }
          )
        );
      }
    }

    const handleBeforeUnload = () => {
      reset({ chartTimes: [] });
      setChartOptions([]);
      setChartTabs((prevTabs) =>
        prevTabs.map((tab) =>
          tab.trendId !== undefined && tab.trendId
            ? tab
            : {
                ...tab,
                chartOptions: [],
                chartTimes: [],
              }
        )
      );
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [trendId, reset, selectedTab]);

  /** 🟢 조건 저장하기 */
  const { mutateAsync: saveChartData } = useMutation({
    mutationFn: async (data: any) => {
      return request<number>({
        method: "POST",
        url: "chart/save",
        data: data,
      });
    },
    onSuccess: (data: number) => {
      updateTrendId(data);
      setModalSaveShow(false);
    },
  });

  // 날짜 변경
  const handleValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSearch((prevSearch: any) => ({
      ...prevSearch,
      [name]: value,
    }));
  };

  // 상태 업데이트를 위한 유틸리티 함수
  const updateChartData = useCallback(
    (newChartTimes: any[]) => {
      // form 상태 업데이트
      setValue("chartTimes", newChartTimes);

      // recoil 상태 업데이트
      setChartTabs((prev) =>
        prev.map((tab) =>
          tab.index === selectedTab
            ? { ...tab, chartTimes: newChartTimes }
            : tab
        )
      );
    },
    [setValue, setChartTabs, selectedTab]
  );

  /** 차트 조건 row 삭제 */
  const handleRemoveRow = useCallback(
    (index: number) => {
      const currentChartTimes = watch("chartTimes") || [];
      const updatedChartTimes = currentChartTimes.filter(
        (_: any, idx: number) => idx !== index
      );

      // 상태 업데이트 함수 호출
      updateChartData(updatedChartTimes);
    },
    [watch, updateChartData]
  );

  /** 차트 조건 row 추가 함수 */
  const handleAddRow = useCallback(() => {
    const currentChartTimes = watch("chartTimes") || [];

    if (currentChartTimes.length >= 10) {
      showAlert("10개 이상은 추가할 수 없습니다.");
      return;
    }

    const nextColorIndex = currentChartTimes.length % PRESET_COLORS.length;

    // 새로운 row 데이터 생성
    const newRow = {
      chtAuto: true,
      chtVisible: true,
      chtColor: PRESET_COLORS[nextColorIndex],
      chtMcyName: "",
      chtLowMcyName: "",
      chtCode: "",
      chtName: "",
      unit: "",
      max: null,
      min: null,
      chtYMax: null,
      chtYMin: null,
    };

    // 상태 업데이트 함수 호출
    updateChartData([...currentChartTimes, newRow]);
  }, [watch, updateChartData]);

  // console.log("@@ DATA: ", newCharts);
  // console.log("@@ WATCH: ", watch("chartTimes"));

  /** 차트 조건 저장하는 함수 */
  const handleSaveChartData = (fileName: string) => {
    saveChartData({
      chartTrend: {
        ...search,
        chtTrdFileName: fileName,
      },
      chartTimes: newCharts,
    });
  };

  /** 차트 데이터 다운로드 함수 */
  const handleExcelDownload = () => {
    setIsExcelLoading(true);
    try {
      const workbook = XLSX.utils.book_new();
      const sheetColumn = [
        "time",
        ...chartOptions.map((chart) => {
          return chart.name;
        }),
      ];

      const sheetData = chartOptions[0].data.map((_, idx) => {
        const timeFormatted = dayjs(chartOptions[0].data[idx].time).format(
          "YYYY-MM-DD HH:mm:ss"
        );

        const rowData = chartOptions.map(
          (option) => option.data[idx]?.value || ""
        );
        return [timeFormatted, ...rowData];
      });

      sheetData.unshift(sheetColumn);

      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet
        // option?.name ? option?.name.substring(0, 30) : `sheet${i + 1}`
      );

      XLSX.writeFile(
        workbook,
        `${search.chtTrdShiptypeName}_${
          search.chtTrdShipName
        }_trendchart_${dayjs().format("YYYYMMDDhhmmss")}.csv`
      );
    } catch (error) {
      showAlert("엑셀 파일 다운로드 중 오류가 발생했습니다.");
    } finally {
      setIsExcelLoading(false);
    }
  };

  const handleConditionFileDownload = () => {
    // const chartTimes = getValues("chartTimes");

    const dataToDownload = {
      newCharts,
      chartTrend: search,
    };

    const json = JSON.stringify(dataToDownload, null, 2);

    const blob = new Blob([json], { type: "application/json" });

    fileDownload(
      blob,
      `${search.chtTrdShiptypeName}_${
        search.chtTrdShipName
      }_trendchart_${dayjs().format("YYYYMMDDhhmmss")}.json`
    );
    // fileDownload(
    //   blob,
    //   `${search.chtTrdShiptypeName}_${
    //     search.chtTrdShipName
    //   }_trendchart_${dayjs().format("YYYYMMDDhhmmss")}.csv`
    // );
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target?.result as string);
        setValue("chartTimes", jsonData.newCharts);
        setChartTabs((prevTabs) =>
          prevTabs.map((tab) =>
            tab.index === selectedTab
              ? {
                  ...tab,
                  chartOptions: [],
                  chartTimes: jsonData.newCharts,
                }
              : tab
          )
        );
        setSearch(jsonData.chartTrend);
      } catch (error) {
        showAlert("조건 업로드 중 오류가 발생했습니다.");
      }
    };

    reader.readAsText(file);
  };

  /** 차트 생성 함수 */
  const onSubmit = (data: any) => {
    const { chartTimes } = data;

    console.log("@@ chartTimes: ", chartTimes);
    console.log("@@ search: ", search);
    createChart(chartTimes, {
      chtTrdShipNo: search?.chtTrdShipNo,
      chtTrdStdTime: search?.chtTrdStdTime,
      chtTrdEndTime: search?.chtTrdEndTime,
    });
  };

  /** 차트 생성 버튼 핸들러 함수  */
  const handleExternalSubmit = async () => {
    const isValid = await trigger();

    if (search?.chtTrdShiptypeNo === "" || search?.chtTrdShipNo === "") {
      showAlert("함형, 함정을 선택해주세요.");
      return;
    }

    const isDateValid = validateDateRange(
      search.chtTrdStdTime,
      search.chtTrdEndTime
    );

    if (isDateValid) {
      if (isValid) {
        if (formRef.current) {
          formRef.current.requestSubmit();
        }
      } else {
        showAlert("장비, 계통, 센서, 색상은 필수값입니다.");
      }
    }
  };

  /** 차트 생성 */
  const createChart = async (
    chartTimes: ChartTime[],
    trendChart: TrendChart
  ) => {
    setChartRenderLoading(true);

    const data: { chartOptions: ChartOption[]; chartTimes: ChartTime[] } =
      await request({
        method: "GET",
        url: "/chart/trend",
        params: {
          chartTimes: chartTimes,
          chartTrend: {
            chtTrdShipNo: trendChart?.chtTrdShipNo,
            chtTrdStdTime: dayjs(trendChart?.chtTrdStdTime).format(
              "YYYY-MM-DD HH:mm:ss"
            ),
            chtTrdEndTime: dayjs(trendChart?.chtTrdEndTime).format(
              "YYYY-MM-DD HH:mm:ss"
            ),
          },
        },
      });

    console.log("@@ data: ", data);

    if (data?.chartOptions) {
      setChartOptions(data?.chartOptions);
      setChartTabs((prevTabs) =>
        prevTabs.map((tab) =>
          tab.index === selectedTab
            ? {
                ...tab,
                chartOptions: data.chartOptions,
                chartSearch: search,
              }
            : tab
        )
      );
    }

    if (data?.chartTimes) {
      reset({ chartTimes: data?.chartTimes });
      setChartTabs((prevTabs) =>
        prevTabs.map((tab) =>
          tab.index === selectedTab
            ? {
                ...tab,
                chartTimes: data.chartTimes,
              }
            : tab
        )
      );
    }

    setChartRenderLoading(false);
  };

  /** 그리드 장비 선택  */
  const EquipSelectRender = (e: any) => {
    return (
      <Controller
        name={`chartTimes[${e.node.rowIndex}].chtMcyName`}
        control={control}
        render={({ field, formState }) => {
          const chartTimes = formState?.errors?.chartTimes as any;
          const error = chartTimes?.[e.node.rowIndex]?.chtMcyName;

          return (
            <>
              <MachinerySelect
                shipNo={search?.chtTrdShipNo}
                value={field.value}
                onChange={(event) => field.onChange(event.target.value)}
                errored={!!error}
              />
            </>
          );
        }}
      />
    );
  };

  /** 그리드 계통 선택  */
  const EquipToolSelectRender = (e: any) => {
    const rowIndex = e.node.rowIndex;
    const machine = watch(`chartTimes[${rowIndex}].chtMcyName`);

    return (
      <Controller
        name={`chartTimes[${rowIndex}].chtLowMcyName`}
        control={control}
        render={({ field, formState }) => {
          const chartTimes = formState?.errors?.chartTimes as any;
          const error = chartTimes?.[e.node.rowIndex]?.chtLowMcyName;
          return (
            <LowMachinerySelect
              machine={machine}
              value={field.value}
              onChange={(event) => {
                field.onChange(event.target.value);
              }}
              errored={!!error}
            />
          );
        }}
      />
    );
  };

  /** 그리드 센서 선택  */
  const SensorSelectRender = (e: any) => {
    const rowIndex = e.node.rowIndex;
    const lowMcyName = watch(`chartTimes[${rowIndex}].chtLowMcyName`);
    const mcyName = watch(`chartTimes[${rowIndex}].chtMcyName`);
    const shipNo = search?.chtTrdShipNo;

    const params = {
      ssrShipNo: shipNo,
      ssrMcyName: mcyName,
      ssrLowMcyName: lowMcyName,
      ssrName: watch(`chartTimes[${rowIndex}].chtCode`),
    };

    const { data: selectOptions } = useQuery<SelectOption[]>({
      queryKey: [GET_SENSOR_SELECT_OPTIONS, params],
      queryFn: async () => {
        if (!params) {
          return [];
        }

        return await request<SelectOption[]>({
          method: "GET",
          url: `/sensor/select/options`,
          params: params,
        });
      },
    });

    return (
      <Controller
        name={`chartTimes[${rowIndex}].chtCode`}
        control={control}
        render={({ field, formState }) => {
          const chartTimes = formState?.errors?.chartTimes as any;
          const error = chartTimes?.[e.node.rowIndex]?.chtCode;
          return (
            <SensorSelect
              params={{
                ssrShipNo: shipNo,
                ssrMcyName: mcyName,
                ssrLowMcyName: lowMcyName,
                ssrName: watch(`chartTimes[${rowIndex}].chtCode`),
              }}
              width={300}
              selectOptions={selectOptions}
              value={field.value}
              onChange={(event) => {
                field.onChange(event.target.value);
                setValue(
                  `chartTimes[${rowIndex}].chtName`,
                  event.target.options[event.target.selectedIndex].text
                );

                const selectDetail: any = selectOptions?.filter((data: any) => {
                  if (data.value === event.target.value) {
                    return data;
                  }
                });

                setValue(`chartTimes[${rowIndex}].unit`, selectDetail[0].unit); // 단위 적용
                setValue(`chartTimes[${rowIndex}].max`, selectDetail[0].max); // 최대값
                setValue(`chartTimes[${rowIndex}].min`, selectDetail[0].min); // 최소값
              }}
              errored={!!error}
            />
          );
        }}
      />
    );
  };

  /** 그리드 Auto */
  const AutoSelectReder = (e: any) => {
    setValue(`chartTimes[${e.node.rowIndex}].chtAuto`, e.data.chtAuto);
    return (
      <Controller
        name={`chartTimes[${e.node.rowIndex}].chtAuto`}
        control={control}
        render={({ field }) => {
          return <input type="checkbox" {...field} checked={!!field.value} />;
        }}
      />
    );
  };

  /** 그리드 NumberBox */
  const NumberBoxRender = (e: any, name: "chtYMax" | "chtYMin") => {
    return (
      <Controller
        name={`chartTimes[${e.node.rowIndex}].${name}`}
        control={control}
        render={({ field }) => (
          <div style={{ width: 120 }}>
            <InputText
              type="number"
              step="any"
              {...field}
              value={field.value}
              defaultValue={field.value}
            />
            {/* <input {...field} /> */}
          </div>
        )}
      />
    );
  };

  /** 그리드 visibleBox */
  const VisibleCheckBoxRender = (e: any) => {
    setValue(`chartTimes[${e.node.rowIndex}].chtVisible`, e.data.chtVisible);
    return (
      <Controller
        name={`chartTimes[${e.node.rowIndex}].chtVisible`}
        control={control}
        render={({ field }) => (
          <input type="checkbox" {...field} checked={!!field.value} />
        )}
      />
    );
  };

  /** 그리드 색상 선택  */
  const ColorSelectRender = (e: any) => {
    setValue(`chartTimes[${e.node.rowIndex}].chtColor`, e.data.chtColor);
    return (
      <Controller
        name={`chartTimes[${e.node.rowIndex}].chtColor`}
        control={control}
        render={({ field, formState }) => {
          const chartTimes = formState?.errors?.chartTimes as any;
          const error = chartTimes?.[e.node.rowIndex]?.chtColor;
          return (
            <input
              type="color"
              style={{
                border: error ? "1px solid red" : "1px solid #d9d9d9",
              }}
              {...field}
              value={field.value ?? ""}
            />
          );
        }}
      />
    );
  };

  /** 그리드 삭제 버튼 */
  const deleteButtonRender = (e: any) => {
    return (
      <Buttons
        type="button"
        size="sm"
        layout="icon"
        icon={<IconDelete />}
        // onClick={() => handleRemoveRow(e.data.no)}
        onClick={() => handleRemoveRow(e.node.rowIndex)}
      />
    );
  };

  const columnDefs: (ColDef | ColGroupDef)[] = [
    {
      headerName: "분석 대상 센서 선택",
      flex: 1,
      children: [
        {
          headerName: "No",
          width: 60,
          valueGetter: (e) => (e?.node?.rowIndex ?? 0) + 1,
          suppressAutoSize: false,
        },
        {
          headerName: "장비",
          cellRenderer: EquipSelectRender,
          width: 140,
          suppressAutoSize: false,
        },
        {
          headerName: "계통",
          cellRenderer: EquipToolSelectRender,
          width: 140,
          suppressAutoSize: false,
        },
        {
          headerName: "센서",
          cellRenderer: SensorSelectRender,
          flex: 1,
          suppressAutoSize: false,
        },
      ],
    },
    {
      headerName: "조회 결과값",
      flex: 1,
      children: [
        {
          headerName: "신호",
          field: "",
          width: 60,
          suppressAutoSize: false,
        },
        {
          headerName: "단위",
          field: "unit",
          width: 60,
          suppressAutoSize: false,
          cellRenderer: (e: any) => {
            return (
              <Controller
                name={`chartTimes[${e.node.rowIndex}].unit`}
                control={control}
                render={({ field }) => (
                  <div>
                    <input
                      type="text"
                      step="any"
                      {...field}
                      value={field.value}
                      defaultValue={field.value}
                      disabled={true}
                      style={{
                        textAlign: "center",
                        border: "none",
                        background: "none",
                      }}
                    />
                  </div>
                )}
              />
            );
          },
        },
        {
          headerName: "최소값",
          field: "min",
          width: 80,
          suppressAutoSize: false,
          cellRenderer: (e: any) => {
            return (
              <Controller
                name={`chartTimes[${e.node.rowIndex}].min`}
                control={control}
                render={({ field }) => (
                  <div>
                    <input
                      type="text"
                      {...field}
                      value={formatNumber(field.value)}
                      defaultValue={formatNumber(field.value)}
                      disabled={true}
                      style={{
                        textAlign: "center",
                        border: "none",
                        background: "none",
                      }}
                    />
                  </div>
                )}
              />
            );
          },
        },
        {
          headerName: "최대값",
          field: "max",
          width: 80,
          cellStyle: { textAlign: "center" },
          suppressAutoSize: false,
          cellRenderer: (e: any) => {
            return (
              <Controller
                name={`chartTimes[${e.node.rowIndex}].max`}
                control={control}
                render={({ field }) => (
                  <div>
                    <input
                      type="text"
                      {...field}
                      value={formatNumber(field.value)}
                      defaultValue={formatNumber(field.value)}
                      disabled={true}
                      style={{
                        textAlign: "center",
                        border: "none",
                        background: "none",
                      }}
                    />
                  </div>
                )}
              />
            );
          },
        },
        {
          headerName: "평균값",
          width: 60,
          suppressAutoSize: false,
        },
      ],
    },

    {
      headerName: "Y축 조건 변경 옵션",
      flex: 1,
      children: [
        {
          headerName: "Auto",
          cellRenderer: AutoSelectReder,
          width: 80,
          suppressAutoSize: false,
        },
        {
          headerName: "Min",
          cellRenderer: (e: any) => NumberBoxRender(e, "chtYMin"),
          width: 110,
          suppressAutoSize: false,
        },
        {
          headerName: "Max",
          field: "chtYMax",
          cellRenderer: (e: any) => NumberBoxRender(e, "chtYMax"),
          width: 110,
          suppressAutoSize: false,
        },
        {
          headerName: "표시",
          cellRenderer: VisibleCheckBoxRender,
          width: 60,
          suppressAutoSize: false,
        },
        {
          headerName: "색상",
          cellRenderer: ColorSelectRender,
          width: 80,
          suppressAutoSize: false,
        },
        {
          headerName: "삭제",
          width: 80,
          cellRenderer: deleteButtonRender,
          suppressAutoSize: false,
        },
      ],
    },
  ];

  // 확장 모드 상태 추가
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  // 확장 모드 토글 함수
  const toggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // 숫자 포맷팅 함수 추가
  const formatNumber = (value: number | null | undefined) => {
    if (value === null || value === undefined) return "";
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  return (
    <>
      <S.ChartContent>
        {/* 첫 번째 차트 영역 */}
        <S.ChartGridBox
          style={{
            height: isExpanded ? "calc(100vh - 280px)" : "auto",
            transition: "height 0.3s ease-in-out",
          }}
        >
          <SearchBar
            gridButtons={
              <>
                {/* <Buttons
                  type="button"
                  size="md"
                  layout="secondary"
                  label="조건 저장하기(DB)"
                  onClick={() => setModalSaveShow(!modalSaveShow)}
                  tooltip={
                    <>
                      '저장하기' 버튼은 트랜드 차트 검색조건 및 조회 결과
                      데이터를 DB에 저장하는 기능을 제공합니다.
                    </>
                  }
                /> */}
                {/* <Buttons
                  type="button"
                  size="md"
                  layout="secondary"
                  label="조건 불러오기(DB)"
                  onClick={() => setModalUploadShow(!modalUploadShow)}
                  tooltip={
                    <>
                      '불러오기' 버튼은 '저장하기' 기능에서 DB 저장된 검색 조건
                      데이터를 불러오는 기능을 제공합니다.
                    </>
                  }
                /> */}
                <Buttons
                  type="button"
                  size="md"
                  layout="highlight"
                  label="차트 데이터 다운로드(CSV)"
                  tooltip={
                    <>
                      '차트 데이터 다운로드' 버튼은 차트를 그릴 때 적용된
                      조회시간값, 센서명, 센서 값을 원본 데이터를 사용자 PC
                      '다운로드' 폴더에 CSV 파일 형태로 저장하는 기능을
                      제공합니다.
                    </>
                  }
                  disabled={isExcelLoading}
                  onClick={handleExcelDownload}
                />

                <Buttons
                  type="button"
                  size="md"
                  layout="primary"
                  label="조건 다운로드(PC)"
                  tooltip={
                    <>
                      '검색조건 다운로드' 기능은 트랜드 차트 검색 조건 및 조회
                      결과 데이터를 사용자 PC '다운로드' 폴더에 저장하는 기능을
                      제공합니다.
                    </>
                  }
                  onClick={handleConditionFileDownload}
                />
                <Buttons
                  type="button"
                  size="md"
                  layout="primary"
                  label="조건 업로드(PC)"
                  tooltip={
                    <>
                      '검색조건 업로드' 버튼은 '검색조건 다운로드' 기능에서
                      사용자 PC에 저장한 이전 트랜드 차트 검색조건 및 조회 결과
                      데이터를 불러오는 기능을 제공합니다.
                    </>
                  }
                  onClick={handleUploadClick}
                />
                {/* <Buttons
                  type="button"
                  size="md"
                  layout="secondary"
                  label="Grafana 이동"
                  tooltip={<>새 탭에 Grafana 사이트를 보여줍니다.</>}
                  onClick={() =>
                    window.open("http://34.2.14.24:3000", "_blank")
                  }
                /> */}
              </>
            }
          />
          <input
            type="file"
            accept=".json"
            ref={fileInputRef}
            onChange={handleFileUpload}
            style={{ display: "none" }}
          />
          <div style={{ position: "relative", flex: 1 }}>
            <MultiAxesLineChart
              chartOptions={newChartOptions}
              chartRenderLoading={chartRenderLoading}
            />

            {/* 확장 모드일 때 차트 영역 내 축소 버튼 표시 */}
          </div>
          {isExpanded && (
            <S.ExpandButtonPosition>
              <Buttons
                type="button"
                size="md"
                layout="secondary"
                label="차트 영역 축소"
                onClick={toggleExpand}
              />
            </S.ExpandButtonPosition>
          )}
        </S.ChartGridBox>

        {/* 두 번째 차트 영역 - 확장 모드에서는 숨김 */}
        {!isExpanded && (
          <S.ChartGridBox>
            <SearchBar
              searchTitle={
                <>
                  트렌드 차트 검색 조건 설정
                  <Buttons
                    type="button"
                    size="sm"
                    layout="warn"
                    label="안내 및 주의사항"
                    onClick={() => setModalWarnShow(!modalWarnShow)}
                  />
                </>
              }
              searchInput={
                <>
                  {/* <InputSelect
                    size="md"
                    placeholder="함형 선택"
                    options={shipTypeSelectOptions}
                    onChange={handleShipTypeSelectChange}
                    value={search?.chtTrdShiptypeNo}
                    required={true}
                  />
                  <InputSelect
                    size="md"
                    placeholder="함정 선택"
                    options={shipSelectOptions}
                    onChange={handleShipSelectChange}
                    value={search?.chtTrdShipNo}
                    required={true}
                  /> */}
                  <InputText
                    type="datetime-local"
                    size="md"
                    name="chtTrdStdTime"
                    defaultValue={search?.chtTrdStdTime}
                    value={search?.chtTrdStdTime}
                    onChange={handleValueChange}
                    step="1"
                    required={true}
                  />
                  <p>~</p>
                  <InputText
                    type="datetime-local"
                    size="md"
                    name="chtTrdEndTime"
                    defaultValue={search?.chtTrdEndTime}
                    value={search?.chtTrdEndTime}
                    onChange={handleValueChange}
                    step="1"
                    required={true}
                  />
                </>
              }
              gridButtons={
                <>
                  {/* 확장/축소 버튼 추가 */}
                  <Buttons
                    type="button"
                    size="md"
                    layout="secondary"
                    onClick={toggleExpand}
                    label={isExpanded ? "차트 영역 축소" : "차트 영역 확장"}
                  />
                  <Buttons
                    type="button"
                    size="md-icon"
                    layout="secondary"
                    icon={<IconPlus />}
                    onClick={handleAddRow}
                  />
                  <Buttons
                    type="button"
                    size="md"
                    layout="primary"
                    label="차트 생성"
                    onClick={handleExternalSubmit}
                  />
                </>
              }
            />
            <S.TrendChartForm ref={formRef} onSubmit={handleSubmit(onSubmit)}>
              <Grid
                rowData={watch("chartTimes") || []}
                columnDefs={columnDefs}
                paging={false}
                loading={chartRenderLoading}
              />
            </S.TrendChartForm>
          </S.ChartGridBox>
        )}
      </S.ChartContent>

      {modalWarnShow && <ModalWarn setModalShow={setModalWarnShow} />}
    </>
  );
};

export default memo(ChartContent);
